import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Shield, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * 2FA verification screen.
 * Props:
 *   tempToken  - short-lived token issued by /auth/login when 2FA is required
 *   onBack()   - return to the login form
 */
const TwoFactorView = ({ tempToken, onBack }) => {
  const [totpCode, setTotpCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const totpInputRef = useRef(null);

  useEffect(() => {
    if (totpInputRef.current) totpInputRef.current.focus();
  }, []);

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Enter a valid 6-digit code');
      return;
    }

    setVerifying2FA(true);
    try {
      const response = await fetch(`${API}/admin/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken, code: totpCode, remember_device: rememberDevice })
      });
      const data = await response.json();

      if (data.success) {
        toast.success(`Welcome, ${data.user?.full_name || 'Admin'}!`);
        window.location.href = '/admin/dashboard';
      } else {
        toast.error(data.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying2FA(false);
      setTotpCode('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-20 px-4">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
            <p className="text-gray-500 text-sm">Enter the code from your authenticator app</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handle2FAVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification Code</label>
                <Input
                  ref={totpInputRef}
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  data-testid="2fa-code-input"
                />
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={verifying2FA || totpCode.length !== 6} data-testid="2fa-verify-btn">
                {verifying2FA ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verify & Login
              </Button>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Remember this device for 30 days</span>
              </label>
              <button
                type="button"
                onClick={onBack}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TwoFactorView;
